import { Injectable } from '@angular/core';
import { Subscription, timer } from 'rxjs';
import { OeAppService } from '../../../shared/oe-app.service';
import { ServerTimeService } from '../../../shared/services/server-time/server-time.service';
import { LiveClassEventDto } from '../../live-class-event-dto';
import { LiveClassEventService } from '../../live-class-event.service';
import { getMinutesFromEpoch, getMsToNextHalfHour } from '../../../shared/date-utils';

const TIME_BETWEEN_FLIP = 60; // 1 hour
const SIGNUP_START_WHEN_CLASS_BEGIN_IS_ZERO = 53;
const SIGNUP_START_WHEN_CLASS_BEGIN_IS_30 = 23;
const MAX_DELAY_BEFORE_NEXT_LIVE_CLASS = 150000;

@Injectable({
  providedIn: 'root',
})
export class LiveClassCountdownTimerService {
  public labelKey: string;
  public msToCountdown: number;

  private subscriptions = new Subscription();
  private classBegin: number;
  private signUpEnd: number;
  private signUpStart: number;
  private waitingForNeedsNewClassesTimeout: boolean = undefined;

  constructor(
    oeApp: OeAppService,
    private serverTime: ServerTimeService,
    private liveClassEventService: LiveClassEventService
  ) {
    this.signUpStart = oeApp.getData('liveClassStudentSignUpStart');
    this.signUpEnd = oeApp.getData('liveClassStudentSignUpEnd');

    this.subscriptions.add(timer(1, 1000).subscribe(() => this.update()));

    this.serverTime.updateServerTimeOffset();

    this.subscribeToLiveClassEventOnChange();
  }

  public destroy() {
    this.subscriptions.unsubscribe();
  }

  private subscribeToLiveClassEventOnChange() {
    this.subscriptions.add(
      this.liveClassEventService.onChange.subscribe((event: LiveClassEventDto) => {
        if (!event) {
          return;
        }
        switch (event.event) {
          case 'WAITING_FOR_FETCH_NEXT_LIVE_CLASSES':
            this.onWaitingForFetchNextLiveClassesEvent();
            break;
          case 'WAITING_FOR_SIGNUP':
            this.onWaitingForSignupEvent(event.data);
            break;
          case 'CAN_SIGN_UP':
            this.onCanSignUpEvent(event.data);
            break;
          case 'HAS_JOIN':
          case 'HAS_REJOIN':
            break;
        }
      })
    );
  }

  private update(): void {
    if (!this.classBegin) {
      return;
    }
    const msToClassBegin = this.classBegin - this.serverTime.offsetNow();
    if (msToClassBegin < this.signUpEnd) {
      // We're at minute 27th or 57th
      if (this.waitingForNeedsNewClassesTimeout === undefined) {
        // It's undefined if the user opened the page when we're at minute 27th or 57th
        this.triggerNextLiveClassEvent();
      } else {
        if (!this.waitingForNeedsNewClassesTimeout) {
          this.liveClassEventService.triggerEvent('WAITING_FOR_FETCH_NEXT_LIVE_CLASSES', null);
        }
      }
    } else {
      this.waitingForNeedsNewClassesTimeout = false;
      if (msToClassBegin < this.signUpStart) {
        const msToSignupEnd = msToClassBegin - this.signUpEnd;
        this.liveClassEventService.triggerEvent('CAN_SIGN_UP', msToSignupEnd);
      } else {
        const msToSignupStart = msToClassBegin - this.signUpStart;
        this.liveClassEventService.triggerEvent('WAITING_FOR_SIGNUP', msToSignupStart);
      }
    }
  }

  private getRandomDelay(): number {
    const msToNextHalfHour = getMsToNextHalfHour();
    const maxMs =
      msToNextHalfHour > MAX_DELAY_BEFORE_NEXT_LIVE_CLASS ? MAX_DELAY_BEFORE_NEXT_LIVE_CLASS : msToNextHalfHour;

    return Math.floor(Math.random() * (maxMs - 10000 + 1)) + 10000;
  }

  private triggerNextLiveClassEvent(): void {
    this.serverTime.updateServerTimeOffset();
    this.waitingForNeedsNewClassesTimeout = false;
    this.liveClassEventService.triggerEvent('NEEDS_NEW_CLASSES', null);
  }

  private onWaitingForFetchNextLiveClassesEvent(): void {
    this.waitingForNeedsNewClassesTimeout = true;
    this.labelKey = 'studybundle.liveClasses.nextLiveClassWaitingMessage';
    const randomDelay = this.getRandomDelay();
    console.log(new Date(), 'Next live classes will be fetched in', Math.round(randomDelay / 1000), 'seconds');
    setTimeout(() => this.triggerNextLiveClassEvent(), randomDelay);
  }

  private onWaitingForSignupEvent(msToSignupStart: number): void {
    this.labelKey = 'studybundle.liveClasses.nextLiveClassMessage';
    this.msToCountdown = msToSignupStart;
  }

  private onCanSignUpEvent(msToSignupEnd: number): void {
    this.labelKey = 'studybundle.liveClasses.signUpEndMessage';
    this.msToCountdown = msToSignupEnd;
  }

  private pad(digit: number): string {
    return digit <= 9 ? `0${digit}` : digit.toString();
  }

  public seconds(): string {
    if (this.msToCountdown && this.msToCountdown > 0) {
      return this.pad(Math.floor(this.msToCountdown / 1000) % 60);
    } else {
      return '00';
    }
  }

  public minutes(signupEnabled: boolean, classBegin: number): string {
    if (this.msToCountdown && this.msToCountdown > 0) {
      if (signupEnabled) {
        return this.pad(Math.floor(this.msToCountdown / (1000 * 60)) % 60);
      } else {
        const timeUntilSignUpEnabled = getTimeUntilSignUpEnabled(
          this.serverTime.offsetNow(),
          this.signUpEnd,
          classBegin
        );
        if (Number(timeUntilSignUpEnabled) > 100) {
          return '00';
        }
        return this.pad(timeUntilSignUpEnabled);
      }
    } else {
      return '00';
    }
  }

  public setClassBegin(value: number) {
    this.classBegin = value;
  }
}

function getTimeUntilSignUpEnabled(serverTime: number, signUpEnd: number, classBegin: number): number {
  const serverPlusSignUpEnd = getServerTimePlusSignupEnd(serverTime, signUpEnd);

  const classBeginMinutes = getMinutesFromEpoch(classBegin);
  let extraTimeUntilSignUpEnabled =
    classBeginMinutes === 0 ? SIGNUP_START_WHEN_CLASS_BEGIN_IS_ZERO : SIGNUP_START_WHEN_CLASS_BEGIN_IS_30;

  return TIME_BETWEEN_FLIP - classBeginMinutes + extraTimeUntilSignUpEnabled - serverPlusSignUpEnd;
}

function getServerTimePlusSignupEnd(serverTime: number, signUpEnd: number): number {
  const serverTimeMinutes = getMinutesFromEpoch(serverTime);
  const signUpEndMinutes = signUpEnd / 1000 / 60;
  let serverPlusSignUpEnd = Math.floor(serverTimeMinutes + signUpEndMinutes);

  // if serverPlusSignUpEnd is greater than 59, subtract 60, we do this because at this moment we get next classes
  if (serverPlusSignUpEnd > 59) {
    serverPlusSignUpEnd = serverPlusSignUpEnd - 60;
  }
  // if serverPlusSignUpEnd is negative, set it to 0
  if (serverPlusSignUpEnd < 0) {
    serverPlusSignUpEnd = 0;
  }
  return serverPlusSignUpEnd;
}



// WEBPACK FOOTER //
// ./ts/app/home/live-class-section/services/live-class-countdown-timer.service.ts